// The AI Agent editor's dialogs and activity feed (components/AIAgent), merged into
// the main dictionary under `editor`: t('editor.<key>'). Same keys in en, es and pt.
import rve from './rve/en.js';
export default {
  // The video editor (components/Editor)
  rve,
  common: {
    cancel: 'Cancel',
    scene: 'Scene {n}',
    sceneSlide: 'Scene {scene} • Slide {slide}',
    chars: '{n} chars',
    unlimited: 'Unlimited',
    left: '{remaining} of {limit} left',
    inPro: 'Included in Pro',
    premiumFeature: 'Premium feature',
    limitReached: 'Limit reached',
    upgradePlan: 'Upgrade Plan',
    updateFailed: 'Update failed',
    uploadFailed: 'Upload failed',
    generationFailed: 'Generation failed',
    unexpectedError: 'An unexpected error occurred.',
    fileUploaded: 'File successfully uploaded.',
    pexelsImage: 'Selected image from Pexels.',
    pexelsVideo: 'Selected video from Pexels.',
    voiceSelection: 'Voice Selection',
    regenerateAudio: 'Regenerate Audio',
    narrationPlaceholder: 'Enter narration text...',
    narrationUpdated: 'Narration updated',
    audioRegenerated: 'Audio regenerated successfully.',
    captionsUpdated: 'Captions updated',
    updateCaptions: 'Update Captions',
    editCaptions: 'Edit Captions',
    mediaLibrary: 'Media Library',
    mediaLibraryHint: 'Search Pexels or upload your own files.',
    aiTools: 'AI Tools',
    generateImage: 'Generate Image',
    generateImageHint: 'Describe your vision and our AI will create it from scratch.',
    prompt: 'Prompt',
    promptPlaceholder: 'A cinematic shot of a futuristic city at sunset...',
    generate: 'Generate',
    uploadingAsset: 'Uploading Asset...',
    generatingImage: 'Generating Image...',
  },

  premium: {
    title: 'Unlock Studio Features',
    body: 'AI Generation, Editing, and Animation are available on our premium plans.',
    viewPricing: 'View Pricing',
  },

  activity: {
    phases: {
      planning: 'Planning',
      concept: 'Concept Development',
      storyboard: 'Storyboard & Research',
      writing: 'Script Writing',
      voiceover: 'Voice Generation',
      visuals: 'Visual Generation',
      transcription: 'Transcription',
      upload: 'Cloud Upload',
      finalizing: 'Finalizing',
    },
    creating: 'Creating your video...',
    complete: 'Generation complete!',
    remaining: '~{time} remaining',
    almostDone: 'Almost there…',
    processing: 'Processing...',
    // What the worker reports, as it words it (the feed shows English as it came;
    // these are for the other languages). A trailing "..." is kept by the feed.
    messages: {
      gettingStarted: 'Getting started',
      ready: 'Ready',
      conceptReady: 'Concept ready',
      footageSelected: 'Footage selected',
      finalizingScript: 'Finalizing script',
      scriptReady: 'Script ready',
      voiceovers: 'Generating voiceovers for {n} scenes',
      voiceoversOne: 'Generating voiceovers for {n} scene',
      allVoiceoversReady: 'All voiceovers ready',
      allVisualsReady: 'All visuals ready',
      visualsReady: 'All {n} visuals ready',
      visualsReadyOne: '{n} visual ready',
      transcribing: 'Transcribing {n} audio tracks',
      transcribingOne: 'Transcribing {n} audio track',
      transcribed: '{n} tracks transcribed',
      transcribedOne: '{n} track transcribed',
      uploadingAssets: 'Uploading assets',
      scenesUploaded: '{n} scenes uploaded',
      scenesUploadedOne: '{n} scene uploaded',
      sceneUploaded: 'Scene {n}/{total} uploaded',
      wrappingUp: 'Wrapping up',
      videoReady: 'Your video is ready',
      failed: 'Failed: {reason}',
      sceneNarration: 'Scene {n}: "{text}"',
      writingScript: 'Writing your video script',
      voiceoversAndVisuals: 'Generating voiceovers and visuals',
      transcribingAudio: 'Transcribing audio',
      finalizingVideo: 'Finalizing your video',
      calculatingDuration: 'Calculating video duration',
      voiceoversComplete: 'Voiceovers complete, generating visuals',
      sceneVisualsReady: 'Scene {n} visuals ready',
      analyzingIdea: 'Analyzing your idea',
      ideaExpanded: 'Idea expanded! Creating video structure',
      polishingScript: 'Polishing and finalizing script',
      scriptComplete: 'Script complete! Preparing scenes',
      searchingFootage: 'Searching for matching footage',
      findingClips: 'Finding the best clips for your video',
      curatingVisuals: 'Curating additional visuals',
      selectingFootage: 'Selecting the best footage for each scene',
      scriptAndFootageReady: 'Script and footage ready',
      transcriptionComplete: 'Transcription complete',
      allAssetsUploaded: 'All assets uploaded',
      sceneLabel: 'Scene {n}',
      sceneVoiced: 'Scene {n} voiced',
    },
  },

  addSlide: {
    dialogTitle: 'Add Slide to Scene {n}',
    title: 'Add Slide',
    added: 'Slide added',
    addFailed: 'Failed to add slide',
    addFailedBody: 'Could not add slide.',
    uploaded: 'Slide uploaded',
    generated: 'Slide generated',
    generatedBody: 'New slide created with AI.',
    limitBody: "You've reached your slide generation limit for this project.",
  },

  asset: {
    dialogTitle: 'Edit Slide - Scene {scene}, Slide {slide}',
    studio: 'Studio',
    editSlide: 'Edit slide',
    invalidDuration: 'Invalid duration',
    invalidDurationBody: 'Must be a positive number.',
    durationUpdated: 'Duration updated',
    durationSet: 'Set to {n}s.',
    durationReset: 'Duration reset',
    durationResetBody: 'Reset to auto-calculated.',
    replaced: 'Asset replaced',
    replaceFailed: 'Failed to replace',
    replaceFailedBody: 'Could not replace asset.',
    uploaded: 'Asset uploaded',
    regenerated: 'Slide regenerated',
    regeneratedBody: 'New image created.',
    regenerateLimit: "You've reached your regeneration limit for this project.",
    edited: 'Slide edited',
    editedBody: 'Image updated successfully.',
    editLimit: "You've reached your editing limit for this project.",
    editFailed: 'Edit failed',
    animateLimit: "You've reached your animation limit for this project.",
    animationFailed: 'Animation failed',
    editImage: 'Edit Image',
    animateVideo: 'Animate Video',
    editImageHint: 'Modify the current slide using natural language instructions.',
    previewAlt: 'Current slide preview',
    editingNote: 'You are editing the current slide image. The AI will preserve the composition where possible.',
    editInstructions: 'Edit Instructions',
    editPlaceholder: 'Make it night time, add rain, change the color of the car...',
    applyEdits: 'Apply Edits',
    animateTitle: 'Animate this slide',
    animateHint: 'Turns the picture into a 5-second moving clip that plays in its place.',
    animate: 'Animate',
    currentAsset: 'Current Asset',
    assetAlt: 'Current slide asset',
    badgeVideo: 'VIDEO',
    badgeImage: 'IMAGE',
    screenTime: 'Screen Time',
    auto: 'Auto',
    seconds: 'sec',
    set: 'Set',
    autoSyncing: 'Currently auto-syncing to voiceover ({n}s).',
    details: 'Details',
    resolution: 'Resolution',
    ratio: 'Ratio',
    enhancingImage: 'Enhancing Image...',
    animatingVideo: 'Animating your slide…',
    animatingWait: '{n} s · usually 15 to 40 seconds',
    animateAgain: 'Animate again',
    animateAgainTitle: 'Animate again',
    stillNote: 'This picture becomes the clip\'s first frame. You can go back to it afterwards.',
    animatedNote: 'This slide is animated. Try another motion from its picture, or go back to the picture.',
    backToPicture: 'Back to the picture',
    animateTime: 'Takes 15 to 40 seconds.',
    animated: 'Slide animated',
    animatedBody: 'It plays in your video now. Not right? Try another motion or go back to the picture.',
    restored: 'Back to the picture',
    restoreFailed: 'Couldn\'t go back to the picture',
    restoring: 'Going back to the picture…',
  },

  animateAll: {
    animating: 'Animating slides',
    complete: 'Slides animated',
    stopped: 'Stopped',
    noneWorked: 'The slides couldn\'t be animated',
    keepsGoing: 'You can close this: it keeps going, and each slide changes in your video as soon as it\'s done.',
    inProject: 'The new clips are in your video. Open a slide to try another motion or go back to its picture.',
    progress: '{current} of {total} animated',
    leftSeconds: 'about {n} s left',
    leftMinutes: 'about {n} min left',
    failed: '{n} didn\'t work',
    stop: 'Stop',
    stopping: 'Stopping after this one…',
    hide: 'Hide',
    retry: 'Try {n} again',
    done: 'Done',
  },

  voiceConfirm: {

    paidOnly: 'Changing the voice of a finished video is on paid plans. You can still pick any voice before you generate the next one.',

    seePlans: 'See plans',
    seconds: '~{n} seconds',
    minuteOne: '~{n} minute',
    minuteMany: '~{n} minutes',
    title: 'Change Voice for All Scenes?',
    description: 'This will regenerate audio for all scenes with the new voice.',
    change: 'Voice Change:',
    impactOne: 'This will regenerate audio for {n} scene',
    impactMany: 'This will regenerate audio for {n} scenes',
    creditsOne: 'Uses {n} voice regeneration credit',
    creditsMany: 'Uses {n} voice regeneration credits',
    time: 'Estimated time: {time}',
    canLeave: 'Runs on our side: you can close the page',
    canUndo: 'You can go back to the current voice afterwards, at no cost',
    currentUsage: 'Current usage:',
    afterChange: 'After this change:',
    remaining: '{n} remaining',
    confirm: 'Confirm & Regenerate',
  },

  narration: {
    dialogTitle: 'Edit Narration - Scene {n}',
    title: 'Edit Narration',
    script: 'Script',
    hint: 'Edit the text below to regenerate the narration.',
  },

  scene: {
    dialogTitle: 'Scene Editor - Scene {n}',
    title: 'Scene Editor',
    narrationTab: 'Narration & Audio',
    mobileTitle: 'Edit Scene {n}',
    script: 'Narration Script',
    hint: 'Edit the text to regenerate the AI voiceover.',
    voiceLimit: "You've reached your voice regeneration limit for this project.",
    captionsBody: 'Timing automatically adjusted.',
    timingTitle: 'Caption Timing',
    timingHint: 'Fix transcription errors. Timing is auto-synced.',
    timingWarning: 'Changes here affect caption display. Keep word count consistent for best results.',
  },

  motion: {
    // How an animated slide moves (components/AIAgent/MotionPicker.jsx)
    label: 'How should it move?',
    picture: 'From the picture',
    pictureHint: 'Moves the way the picture was described',
    subtle: 'Subtle motion',
    subtleHint: 'The picture comes alive, the camera stays put',
    zoom: 'Slow zoom in',
    zoomHint: 'The camera moves slowly toward the subject',
    subject: 'Subject moves',
    subjectHint: 'People and animals breathe, blink and gesture',
    dramatic: 'Dramatic',
    dramaticHint: 'A sweeping camera move with lots of motion',
    custom: 'Describe it',
    customHint: 'Say in your own words how it moves',
    customPlaceholder: 'e.g. Snow falls gently while the lantern flickers',
  },

  slides: {
    title: 'Animate slides',
    subtitle: 'Pick pictures to turn into short moving clips. Each takes about 20 seconds.',
    pick: 'Pictures to animate',
    picked: '{selected} of {total} picked',
    selectAll: 'Select all',
    clear: 'Clear',
    left: '{remaining} of {limit} animations left in this project',
    noneLeft: 'You\'ve used all the animations in this project.',
    upgrade: 'Upgrade',
    allMoving: 'Every slide in this video already moves.',
    movingOne: '{n} slide already moves (a video clip or an animation), so it isn\'t shown.',
    movingMany: '{n} slides already move (video clips or animations), so they aren\'t shown.',
    underMinute: 'Under a minute',
    aboutMinutes: 'About {n} min',
    pickFirst: 'Pick slides to animate',
    animateOne: 'Animate {n} slide',
    animateMany: 'Animate {n} slides',
    thumbAlt: 'Scene {scene} - Slide {slide}',
  },

  captionsEdit: {
    confirm: 'Significant changes detected. Timing might be affected. Continue?',
    updatedBody: 'Timing has been automatically adjusted.',
    dialogTitle: 'Edit Captions - Scene {n}',
    subtitle: 'Scene {n} • Syncs with Audio',
    warning: 'Editing text here will automatically re-align the caption timing. Try to keep the word count similar to preserve the flow.',
    placeholder: 'Enter caption text...',
    words: '{n} words',
    autoSync: 'Auto-sync enabled',
  },

  voicePicker: {
    title: 'Select Narrator Voice',
    description: 'Choose a voice for your video narration',
    confirm: 'Confirm',
  },

  // Caption style controls (VideoStudio), shared with the other editors
  captionStyle: {
    previewYour: 'Your',
    previewStory: 'Story',
    templates: {
      keyword: 'Keyword',
      minimal: 'Minimal',
      neon: 'Neon',
      tiktok: 'TikTok',
      comic: 'Comic',
      elegant: 'Elegant',
      glitch: 'Glitch',
      bold: 'Bold',
      clean: 'Clean',
      outline: 'Outline',
      punch: 'Punch',
      redbox: 'Red box',
      boxed: 'Boxed',
      podcast: 'Podcast',
      pop: 'Pop',
      mint: 'Mint',
    },
    position: 'Position',
    fontText: 'Font & Text',
    colors: 'Colors',
    text: 'Text',
    highlight: 'Highlight',
    stroke: 'Stroke',
    background: 'Background',
    animationTiming: 'Animation & Timing',
    speed: 'Speed',
    speedAria: 'Word Grouping Speed',
    words: 'Words',
    wordsAria: 'Max Words Per Page',
    spacing: 'Spacing',
    lineHeight: 'Line Height',
    letterSpacing: 'Letter Spacing',
    transforms: 'Transforms',
    rotation: 'Rotation',
    skew: 'Skew',
    scale: 'Scale',
  },

  // The Pexels search and upload picker inside the AI Agent's scene dialogs
  mediaPicker: {
    searchTab: 'Search Pexels',
    uploadTab: 'Upload a file',
    images: 'Images',
    videos: 'Videos',
    searchImages: 'Search images…',
    searchVideos: 'Search videos…',
    search: 'Search',
    orientation: 'Orientation',
    anyOrientation: 'Any orientation',
    landscape: 'Landscape',
    portrait: 'Portrait',
    square: 'Square',
    size: 'Size',
    anySize: 'Any size',
    small: 'Small',
    medium: 'Medium',
    large: 'Large',
    foundImages: '{count} images found',
    foundVideos: '{count} videos found',
    page: 'Page {page}',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    promptImagesTitle: 'Search for images',
    promptVideosTitle: 'Search for videos',
    promptImagesBody: 'Type what you need above to find free images on Pexels.',
    promptVideosBody: 'Type what you need above to find free videos on Pexels.',
    noImages: 'No images found for “{query}”',
    noVideos: 'No videos found for “{query}”',
    select: 'Select',
    photoBy: 'Photo by {name}',
    videoBy: 'Video by {name}',
    by: 'by {name}',
    poweredBy: 'Powered by',
    uploadTitle: 'Upload your own',
    uploadImage: 'Choose an image from your device.',
    uploadVideo: 'Choose a video from your device.',
    uploadBoth: 'Choose an image or a video from your device.',
    chooseFile: 'Choose a file',
    previewImage: 'Preview image',
    previewVideo: 'Preview video',
    close: 'Close',
    cancel: 'Cancel',
    addImage: 'Use this image',
    addVideo: 'Use this video',
    loadFailedTitle: "Couldn't load results",
    loadFailedBody: "Pexels didn't answer. Try again in a moment.",
    wrongTypeTitle: "That file won't work here",
    wrongTypeImage: 'Choose an image file.',
    wrongTypeVideo: 'Choose a video file.',
    wrongTypeBoth: 'Choose an image or a video file.',
  },
};
